import { Link } from 'react-router-dom';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';

import FeedbackField from './FeedbackField';
import {
  FEEDBACK_LINK,
  FEEDBACK_TYPES,
  IDENTITY_MODES,
  MAX_DISPLAY_NAME_LENGTH,
  MAX_GUEST_USERNAME_LENGTH,
} from './constants';

const FeedbackIdentitySection = ({
  feedbackType,
  identityMode,
  setIdentityMode,
  hasAccountSession,
  username,
  guestUsername,
  setGuestUsername,
  displayName,
  setDisplayName,
  isAnonymous,
  fieldsDisabled,
  clearStatus,
  returnPath,
}) => {
  const sessionRequiresAccount = feedbackType === FEEDBACK_TYPES.SESSION;

  return (
    <Box>
      <FormControl component="fieldset" sx={{ width: '100%' }} disabled={fieldsDisabled}>
        <FormLabel
          component="legend"
          sx={{
            mb: 1.5,
            color: 'inherit',
            fontWeight: 700,
            '&.Mui-focused': { color: 'inherit' },
            '&.Mui-disabled': { color: 'inherit' },
          }}
        >
          How should we identify you?
        </FormLabel>
        <RadioGroup
          name="identityMode"
          value={identityMode}
          onChange={(e) => {
            setIdentityMode(e.target.value);
            clearStatus();
          }}
        >
          {hasAccountSession && (
            <FormControlLabel
              value={IDENTITY_MODES.ACCOUNT}
              control={<Radio color="warning" />}
              label={`Use my account (${username || 'logged in'})`}
            />
          )}
          <FormControlLabel
            value={IDENTITY_MODES.USERNAME}
            control={<Radio color="warning" />}
            label="Enter my username or email"
          />
          {!sessionRequiresAccount && (
            <FormControlLabel
              value={IDENTITY_MODES.ANONYMOUS}
              control={<Radio color="warning" />}
              label="Submit anonymously"
            />
          )}
        </RadioGroup>
      </FormControl>

      {sessionRequiresAccount && (
        <Typography mt={1.5} fontSize={14} sx={{ opacity: 0.8, lineHeight: 1.5 }}>
          Session feedback is tied to the member who completed the workout, so anonymous submit
          isn’t available here.
        </Typography>
      )}

      {!hasAccountSession && identityMode !== IDENTITY_MODES.ANONYMOUS && (
        <Typography mt={1.5} fontSize={14} sx={{ opacity: 0.8 }}>
          Prefer to use your account?{' '}
          <Link
            to="/signin"
            state={{ from: returnPath }}
            style={{ color: FEEDBACK_LINK, textDecoration: 'underline' }}
          >
            Log in
          </Link>
        </Typography>
      )}

      {identityMode === IDENTITY_MODES.USERNAME && (
        <FeedbackField
          sx={{ mt: 2 }}
          required
          autoComplete="username"
          label="Username or email"
          helperText={
            sessionRequiresAccount
              ? 'Use the account that owns this workout so we can attribute it correctly.'
              : 'We’ll attach this to an account if we find one; otherwise we’ll still send it through.'
          }
          placeholder="Your Black Box VR username or email"
          value={guestUsername}
          disabled={fieldsDisabled}
          maxLength={MAX_GUEST_USERNAME_LENGTH}
          onChange={(e) => {
            setGuestUsername(e.target.value);
            clearStatus();
          }}
        />
      )}

      {identityMode === IDENTITY_MODES.ANONYMOUS && (
        <Typography mt={1.5} fontSize={14} sx={{ opacity: 0.75, lineHeight: 1.5 }}>
          Submitted without your name or account. We won’t be able to follow up with you.
        </Typography>
      )}

      {!isAnonymous && (
        <FeedbackField
          sx={{ mt: 3 }}
          label="Display name (optional)"
          placeholder={
            identityMode === IDENTITY_MODES.ACCOUNT
              ? username || 'Your name'
              : guestUsername || 'Your name'
          }
          value={displayName}
          disabled={fieldsDisabled}
          maxLength={MAX_DISPLAY_NAME_LENGTH}
          onChange={(e) => {
            setDisplayName(e.target.value);
            clearStatus();
          }}
        />
      )}
    </Box>
  );
};

export default FeedbackIdentitySection;
